import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import { PropsWithChildren } from 'react';
import { useIncident } from 'components/IAP/hooks/incidentHooks';
import { useIapParams } from 'components/IAP/hooks/useIapParams';
import { PHASE_LIST } from 'components/IAP/planningView/USCGPage';
import { useUscgMapContext } from 'components/MapPage/CorporateViewTabs/LayersTab/UscgMapContext';
import { useUserPositions } from 'hooks/incident/useUserPosition';
import { NULL_VALUE } from 'constants/displayConstants';
import { useIsCommandCenter } from 'components/MapPage/CorporateViewTabs/LayersTab/utils/isCommandCenter';
import {
  selectGroupGuid,
  selectUserGuid,
  useAppSelector,
} from 'slices/commonSelectors';
import { useGetRostersForGroup } from 'hooks/incident/useRoster';
import { useAors } from 'hooks/useAors';
import { useAutoZoomToHomeAor } from './CorporateViewTabs/AorsTab/utils/useAutoZoomToHomeAor';

type MapSidebarContainerProps = {
  isMainSidebarVisible: boolean;
  setIsMainSidebarVisible: (visible: boolean) => void;
};

export const MapSidebarContainer = ({
  children,
  isMainSidebarVisible,
  setIsMainSidebarVisible,
}: PropsWithChildren<MapSidebarContainerProps>) => {
  return isMainSidebarVisible ? (
    <>
      <div className="d-flex align-items-center" style={{ width: '50%' }}>
        <div className="map-left-sidebar">{children}</div>
        {setIsMainSidebarVisible && (
          <StylishNewButton
            className="button--secondary map-sidebar-button"
            style={{ backgroundColor: 'var(--background-dark)' }}
            onClick={() => {
              setIsMainSidebarVisible(false);
            }}
          >
            <SharedIcon iconName="chevron_left" size="sm" />
          </StylishNewButton>
        )}
      </div>
    </>
  ) : (
    <StylishNewButton
      className="button--secondary map-sidebar-button map-sidebar-button-in"
      style={{ backgroundColor: 'var(--background-dark)' }}
      onClick={() => {
        setIsMainSidebarVisible(true);
      }}
    >
      <SharedIcon iconName="chevron_right" size="sm" />
    </StylishNewButton>
  );
};

export const MapExitButtonGroup = ({ children }: PropsWithChildren) => {
  const { mapView, setIsMapVisible } = useUscgMapContext();
  const { zoomToHomeAor } = useAutoZoomToHomeAor();
  const { data: incident } = useIncident();
  const { data: aors } = useAors();
  const savedParams = useIapParams(incident?.id || '');
  const positions = useUserPositions();
  const isCommandCenter = useIsCommandCenter();
  const userGuid = useAppSelector(selectUserGuid);
  const groupGuid = useAppSelector(selectGroupGuid);
  const { data: roster } = useGetRostersForGroup(groupGuid);

  const currentUserRoster = roster?.find((r) => r.user_guid === userGuid);
  const homeAorId = currentUserRoster?.home_aor ?? null;

  const homeAor = aors?.find((a) => a.id === homeAorId);
  const aorGeoJson = homeAor?.geolocation?.geojson?.data;

  const flyTo = () => {
    if (
      incident?.location?.type === 'Point' ||
      incident?.location?.type === 'Polygon'
    ) {
      const coords = incident.location.coordinates;
      mapView?.goTo({ center: coords, zoom: 7 });
    }
  };

  return (
    <>
      {/* Incident-specific labels — hidden in corporate/command-center view */}
      {!isCommandCenter && (
        <>
          <div className="map-sector-label">
            Incident: {incident?.name || NULL_VALUE}
            {incident?.location && (
              <StylishNewButton
                onClick={() => flyTo()}
                className="button--link text-white"
                aria-label="Fly to incident location"
              >
                <SharedIcon iconName="map" />
              </StylishNewButton>
            )}
          </div>
          {savedParams?.phase !== undefined && savedParams?.phase !== null && (
            <div className="map-sector-label">
              Phase {savedParams.phase + 1}:{' '}
              {PHASE_LIST[savedParams.phase] || 'Unknown Phase'}
            </div>
          )}
        </>
      )}

      {/* My Positions and Home AOR — only shown in the Hub (command center) */}
      {isCommandCenter && (
        <>
          <div className="map-sector-label">
            My Positions: {positions.join(', ')}
          </div>
          <div
            className="map-sector-label"
            role="button"
            onClick={zoomToHomeAor}
            style={{
              cursor: aorGeoJson ? 'pointer' : 'default',
            }}
          >
            My AOR: {homeAor?.name || NULL_VALUE}
            {aorGeoJson && <SharedIcon iconName="map" />}
          </div>
        </>
      )}

      {/* Exit Map — text hidden below md to save space */}
      <StylishNewButton
        className="button--secondary"
        onClick={() => setIsMapVisible(false)}
        style={{
          backgroundColor: 'var(--background-dark)',
          color: 'var(--text-white)',
        }}
      >
        <SharedIcon iconName="close" />
        <span className="d-none d-md-inline">Exit Map</span>
      </StylishNewButton>
      {children}
    </>
  );
};
